// Shared renderer for /moj-translator/faq (TH) and /en/moj-translator/faq (EN).
// Reuses VoiceSearchFAQ for QAPage + SpeakableSpecification schema.
import { Link } from "@tanstack/react-router";
import { VoiceSearchFAQ } from "@/components/seo/VoiceSearchFAQ";
import { MOJ_VOICE_FAQS } from "@/data/moj-voice-faqs";
import { MOJ_KEYWORD_CLUSTERS, MOJ_MISSPELLINGS } from "@/data/moj-keyword-glossary";

type Props = { lang: "th" | "en" };

export function MojFaqHub({ lang }: Props) {
  const th = lang === "th";
  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:py-14">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-gold">
          {th ? "MoJ Translator · ศูนย์คำถาม & คำค้น" : "MoJ Translator · Q&A & keyword hub"}
        </p>
        <h1 className="mt-2 text-3xl font-extrabold leading-tight sm:text-4xl">
          {th
            ? "นักแปลกระทรวงยุติธรรม — คำถามที่พบบ่อย & ศูนย์คำค้น 300 คีย์เวิร์ด"
            : "Ministry of Justice Certified Translator — Q&A & 300-keyword hub"}
        </h1>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-muted-foreground">
          {th
            ? "รวมคำตอบเชิงลึกจัดกลุ่มตาม 8 คลัสเตอร์คำค้นหา 300 คีย์เวิร์ด: คดีความ, นิติกรรมสัญญา, การรับรองความถูกต้อง, ราคา/ความเร็ว, สำนักงานกฎหมายต่างประเทศ, วัตถุประสงค์เฉพาะ และคำถามที่พบบ่อย — แปลโดยนักแปล MoJ ประจำ 6 ท่าน ตราประทับได้รับการยอมรับในชั้นศาลไทย"
            : "Deep answers across 8 keyword clusters (300 keywords): litigation files, business contracts, certification & credibility, price & speed, foreign law firms, long-tail use cases, and FAQs — produced by 6 in-house MoJ translators whose stamp is accepted by Thai courts."}
        </p>
        <nav className="mt-5 flex flex-wrap gap-2 text-sm">
          <Link to={th ? "/moj-translator" : "/en/moj-translator"} className="rounded-full border px-3 py-1 hover:bg-muted">
            {th ? "ศูนย์บริการหลัก" : "Main hub"}
          </Link>
          <Link to={th ? "/notary" : "/en/notary"} className="rounded-full border px-3 py-1 hover:bg-muted">
            {th ? "Notary Public" : "Notary Public"}
          </Link>
          <Link to={th ? "/sworn-translator" : "/en/sworn-translator"} className="rounded-full border px-3 py-1 hover:bg-muted">
            {th ? "Sworn Translator" : "Sworn Translator"}
          </Link>
        </nav>
      </header>

      <section aria-labelledby="clusters-h" className="mb-10">
        <h2 id="clusters-h" className="mb-4 text-2xl font-bold">
          {th ? "8 คลัสเตอร์คำค้นหา (300 คีย์เวิร์ด)" : "8 keyword clusters (300 keywords)"}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {MOJ_KEYWORD_CLUSTERS.map((c) => (
            <article key={c.id} className="rounded-2xl border bg-card p-5 shadow-sm transition hover:shadow-md">
              <h3 className="text-lg font-bold">{th ? c.titleTh : c.titleEn}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {th ? c.descTh : c.descEn}
              </p>
              <ul className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                {c.keywords.map((k) => (
                  <li key={k} className="rounded-full bg-muted px-2 py-0.5 font-mono">
                    {k}
                  </li>
                ))}
              </ul>
              <Link to={th ? c.linkTh : c.linkEn} className="mt-4 inline-block text-sm font-semibold text-primary hover:underline">
                {th ? "ดูรายละเอียด →" : "See details →"}
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="mm-h" className="mb-10 rounded-2xl border bg-muted/40 p-5">
        <h2 id="mm-h" className="text-xl font-bold">
          {th ? "คำที่คนไทยมักสะกดผิด (เราเข้าใจทุกแบบ)" : "Thai spellings we accept"}
        </h2>
        <ul className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          {MOJ_MISSPELLINGS.map((m) => (
            <li key={m.wrong} className="flex items-baseline gap-2">
              <span className="rounded bg-background px-2 py-0.5 font-mono text-xs" data-normalized-from={m.wrong}>
                {m.wrong}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="font-semibold">{m.right}</span>
            </li>
          ))}
        </ul>
      </section>

      <VoiceSearchFAQ
        topic={th ? "นักแปลกระทรวงยุติธรรม (MoJ)" : "Ministry of Justice Certified Translator"}
        faqs={MOJ_VOICE_FAQS}
        lang={lang}
      />

      <section className="mt-10 rounded-2xl border border-gold/40 bg-gradient-to-br from-navy-deep to-navy-deep/95 p-6 text-white">
        <h2 className="text-xl font-bold">
          {th ? "ยังไม่เจอคำตอบ? ปรึกษาทีมทนายฟรี" : "Still need help? Talk to our lawyers"}
        </h2>
        <p className="mt-2 text-sm text-white/85">
          {th
            ? "LINE @nycli · โทร +66-83-249-4999 · อีเมล nycli@ilc.ltd · เปิด จ.–ส. 09:00–18:00 ICT · ที่ปรึกษาโดยทนายไทย + นักแปล MoJ ทุกคดี"
            : "LINE @nycli · Call +66-83-249-4999 · Email nycli@ilc.ltd · Mon–Sat 09:00–18:00 ICT · Every consultation reviewed by a Thai-licensed lawyer and an MoJ translator."}
        </p>
      </section>
    </main>
  );
}
